import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Sparkles, 
  Calculator, 
  Search, 
  Activity, 
  Award, 
  ChevronRight, 
  HelpCircle, 
  ChevronDown,
  Landmark,
  Coins,
  HeartHandshake,
  Wallet,
  Receipt,
  Briefcase,
  Building,
  ShieldAlert,
  Shield,
  FileText,
  Check,
  BookOpen,
  PiggyBank,
  TrendingUp,
  CreditCard
} from "lucide-react";
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip as ChartTooltip 
} from "recharts";

import SEO from "../seo/SEO";
import { allTools, getSlug } from "../data/allTools";
import { categories } from "../data/categories";
import { HeroSection } from "../components/hero/Hero";

// Helper to look up active Lucide category icons with premium context overrides
export const CategoryIcon = ({ 
  iconName, 
  className = "w-5 h-5",
  categoryId,
  toolName
}: { 
  iconName: string; 
  className?: string;
  categoryId?: string;
  toolName?: string;
}) => {
  const normName = toolName?.toLowerCase() || "";
  const catId = categoryId || "";

  if (catId === "savings" || catId === "banking" || catId === "networth") {
    if (normName.includes("invest") || normName.includes("growth") || normName.includes("compound") || normName.includes("sip") || normName.includes("yield")) {
      return <TrendingUp className={className} />;
    }
    return <PiggyBank className={className} />;
  }
  if (catId === "retirement") {
    return <Shield className={className} />;
  }
  if (catId === "loans" && (normName.includes("credit") || normName.includes("debt") || normName.includes("payoff"))) {
    return <CreditCard className={className} />;
  }
  if (catId === "loans") {
    return <Landmark className={className} />;
  }
  if (catId === "tax") {
    return <Calculator className={className} />;
  }

  const normIcon = iconName.trim();
  switch (normIcon) {
    case "Landmark":
      return <Landmark className={className} />;
    case "Coins":
      return <Coins className={className} />;
    case "HeartHandshake":
      return <HeartHandshake className={className} />;
    case "Wallet":
      return <Wallet className={className} />;
    case "Receipt":
      return <Receipt className={className} />;
    case "Briefcase":
      return <Briefcase className={className} />;
    case "Building":
      return <Building className={className} />;
    case "ShieldAlert":
      return <ShieldAlert className={className} />;
    case "Shield":
      return <Shield className={className} />;
    default:
      return <Calculator className={className} />;
  }
};

// Popularity indicator for premium SaaS presentation
export const isPopularTool = (tool: { name: string; isHighCPC?: boolean }) => {
  const normName = tool.name.toLowerCase();
  return (
    !!tool.isHighCPC ||
    normName.includes("mortgage") ||
    normName.includes("compound interest") ||
    normName.includes("debt payoff") ||
    normName.includes("retirement") ||
    normName.includes("budget planner") ||
    normName === "loan calculator"
  );
};

// Trust badges for top-tier financial calculators
export const getToolBadge = (toolId: string) => {
  switch (toolId) {
    case "mortgage-payment":
    case "compound-interest":
    case "hourly-salary":
      return { text: "Featured", className: "bg-blue-500/10 text-blue-400 border border-blue-500/20" };
    case "auto-loan":
    case "debt-payoff":
    case "retirement-planner":
      return { text: "Popular", className: "bg-amber-500/10 text-amber-400 border border-amber-500/20" };
    case "cd-ladder":
    case "rent-affordability":
    case "simple-investment":
      return { text: "Most Used", className: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" };
    default:
      return null;
  }
};

// Premium Section headers layout
export const categoryHeaderDetails: Record<string, { title: string; desc: string }> = {
  loans: {
    title: "Loan & Mortgage Calculators",
    desc: "Estimate payments, affordability, and borrowing costs with accurate scheduling models."
  },
  savings: {
    title: "Savings & Investment Calculators",
    desc: "Plan, grow, and manage your money with powerful compound growth and SIP models."
  },
  retirement: {
    title: "Retirement & FIRE Planning",
    desc: "Project nest eggs, target timelines, and calculate realistic post-career monthly expenses."
  },
  budget: {
    title: "Budget & Expense Calculators",
    desc: "Model dynamic household budgets, rent affordability guidelines, and emergency reserves."
  },
  tax: {
    title: "Document & Tax Calculators",
    desc: "Approximate federal brackets, state adjustments, gains, and property dues."
  },
  income: {
    title: "Income & Salary Calculators",
    desc: "Evaluate take-home pay structures, overtime logs, and hourly transformations."
  },
  banking: {
    title: "Banking & CD Ladders",
    desc: "Optimize high-yield accounts, certificate of savings ladders, and interest pipelines."
  },
  networth: {
    title: "Net Worth & Wealth Velocity",
    desc: "Assess total wealth indicators, comprehensive balance sheets, and asset valuations."
  },
  insurance: {
    title: "Insurance Coverage Calculators",
    desc: "Audit health premiums, life payouts, and property damage liabilities."
  }
};

interface HomeProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export default function Home({
  searchQuery,
  setSearchQuery,
}: HomeProps) {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // List of filtered tools in real-time
  const filteredTools = useMemo(() => {
    return allTools.filter(tool => {
      const matchesCategory = selectedCategory === "all" || tool.category === selectedCategory;
      const matchesSearch = 
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.guide.overview.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  // Group filtered tools into sorted category buckets
  const groupedTools = useMemo(() => {
    const groups: Map<string, typeof allTools> = new Map();
    categories.forEach(cat => {
      groups.set(cat.id, []);
    });
    
    filteredTools.forEach(tool => {
      const list = groups.get(tool.category) || [];
      list.push(tool);
      groups.set(tool.category, list);
    });

    const activeGroups: Array<{
      category: typeof categories[0];
      tools: typeof allTools;
    }> = [];

    categories.forEach(cat => {
      const toolsInCat = groups.get(cat.id) || [];
      if (toolsInCat.length > 0) {
        activeGroups.push({
          category: cat,
          tools: toolsInCat
        });
      }
    });

    return activeGroups;
  }, [filteredTools]);

  // Pre-calculate exact category calculator counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allTools.forEach(tool => {
      counts[tool.category] = (counts[tool.category] || 0) + 1;
    });
    return counts;
  }, []);

  // --- GROWTH SCENARIO SIMULATOR STATE ---
  const [simulatorMonthly, setSimulatorMonthly] = useState<number>(500);
  const [simulatorYears, setSimulatorYears] = useState<number>(10);

  const simulatorData = useMemo(() => {
    const P = 10000; // Starting reserve
    const r = 0.080; // 8% Return rate
    const n = 12; // Compounded monthly
    const totalMonths = simulatorYears * 12;

    const dataPoints = [];
    const step = Math.max(1, Math.floor(totalMonths / 6));

    for (let month = 0; month <= totalMonths; month += step) {
      const yearFraction = month / 12;
      
      const compoundedStarting = P * Math.pow(1 + r/n, month);
      
      let compoundedAdditions = 0;
      if (month > 0) {
        compoundedAdditions = simulatorMonthly * ((Math.pow(1 + r/n, month) - 1) / (r/n)) * (1 + r/n);
      }
      
      const total = compoundedStarting + compoundedAdditions;
      dataPoints.push({
        name: `Y${Math.round(yearFraction)}`,
        value: Math.round(total)
      });
    }

    const finalStarting = P * Math.pow(1 + r/n, totalMonths);
    const finalAdditions = simulatorMonthly * ((Math.pow(1 + r/n, totalMonths) - 1) / (r/n)) * (1 + r/n);
    const finalTotal = finalStarting + finalAdditions;
    
    if (dataPoints[dataPoints.length - 1]?.name !== `Y${simulatorYears}`) {
      dataPoints.push({
        name: `Y${simulatorYears}`,
        value: Math.round(finalTotal)
      });
    } else {
      dataPoints[dataPoints.length - 1].value = Math.round(finalTotal);
    }

    const pctIncrease = Math.round(((finalTotal - P) / P) * 100);

    return {
      total: Math.round(finalTotal),
      pctIncrease,
      chart: dataPoints
    };
  }, [simulatorMonthly, simulatorYears]);

  // High CPC/Easy Ranking highlighted tools
  const spotlightTools = useMemo(() => {
    return allTools.filter(t => t.isHighCPC).slice(0, 4);
  }, []);

  return (
    <div className="space-y-16">
      <SEO 
        title="USMoneyAI - Free Financial Calculators &amp; Money Tools" 
        description="Free financial calculators for loans, mortgages, debt payoff, savings, investments, retirement planning, and personal finance decisions."
      />

      {/* Interactive Hero Section */}
      <HeroSection 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery} 
        onExploreClick={() => { 
          setSelectedCategory("all"); 
          document.getElementById("calculator-directory-title")?.scrollIntoView({ behavior: "smooth" }); 
        }} 
      />

      {/* Platform Features Section with smooth anchor positioning */}
      <section id="features-section" className="scroll-mt-24 pt-12 text-left border-t border-slate-900/60">
        <div className="space-y-8">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-amber-500/20 bg-amber-950/20 text-xs font-mono text-amber-400">
              <Award className="w-3.5 h-3.5 text-amber-500" />
              Platform Capabilities
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-medium text-white tracking-tight">
              Why Financial Planners Choose USMoneyAI
            </h2>
            <p className="text-slate-400 text-xs sm:text-sm max-w-xl mx-auto font-light leading-relaxed">
              Explore the core principles and premium design choices that make our site a trusted financial calculator guide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-950/45 border border-slate-800/80 p-6 rounded-2xl space-y-4 hover:border-amber-500/20 hover:bg-slate-950/80 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/25 text-amber-500">
                <Calculator className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-100 font-sans">77+ Free Calculators</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed font-light">
                Complete calculators for mortgages, vehicle loans, payment schedules, high-retention CD ladders, and wealth pathways.
              </p>
            </div>

            <div className="bg-slate-950/45 border border-slate-800/80 p-6 rounded-2xl space-y-4 hover:border-amber-500/20 hover:bg-slate-950/80 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/25 text-amber-500">
                <Shield className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-100 font-sans">Absolute Client Security</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed font-light">
                We believe in zero data leaks. Your private mortgage inputs and tax brackets run completely browser-side. No details ever touch external servers.
              </p>
            </div>

            <div className="bg-slate-950/45 border border-slate-800/80 p-6 rounded-2xl space-y-4 hover:border-amber-500/20 hover:bg-slate-950/80 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/25 text-amber-500">
                <Activity className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-100 font-sans">Amortization Pathways</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed font-light">
                Visualize total debt breakdowns with elegant interactive charts, monthly schedules, and granular breakdowns of every interest cent.
              </p>
            </div>

            <div className="bg-slate-950/45 border border-slate-800/80 p-6 rounded-2xl space-y-4 hover:border-amber-500/20 hover:bg-slate-950/80 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/25 text-amber-500">
                <BookOpen className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-slate-100 font-sans">Professional Analytics</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed font-light">
                All math logic corresponds to standard compound laws and tax amortizations. Regular validation provides reliable results matching premium SaaS portfolios.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Search Directory Searchbar & Category Tabs slider */}
      <div id="calculator-directory-title" className="space-y-8 pt-6 scroll-mt-24 text-left">
        
        {/* Prominent Search Bar Section directly above categories */}
        <div className="bg-gradient-to-br from-[#111827] to-slate-950/90 p-6 sm:p-8 rounded-2xl border border-slate-800/80 shadow-xl space-y-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white flex items-center gap-2">
                <Search className="w-5.5 h-5.5 text-amber-500" />
                <span>Search Calculator Directory</span>
              </h3>
              <p className="text-slate-400 text-xs sm:text-sm mt-1">
                Filter our complete analytical library instantly by topic, keyword, or instrument class.
              </p>
            </div>
            {searchQuery && (
              <span className="text-[11px] font-mono font-semibold bg-amber-500/10 text-amber-500 border border-amber-500/20 px-3.5 py-1 rounded-full shrink-0">
                Matched {filteredTools.length} {filteredTools.length === 1 ? 'Calculator' : 'Calculators'}
              </span>
            )}
          </div>
          
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
            <input 
              type="text"
              placeholder="Search 77+ financial calculators (e.g. mortgage, CD ladder, compound interest, SIP, retirement...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#070b13] border border-slate-800/80 focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/30 py-4.5 pl-12 pr-4 rounded-xl text-sm sm:text-base text-slate-100 outline-none transition-all placeholder:text-slate-600 shadow-inner"
            />
          </div>
        </div>

        {/* Spaced categories with dynamic counters */}
        <div className="space-y-3.5 pt-2">
          <p className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest">
            FILTER PLATFORM CATEGORIES
          </p>
          <div className="flex flex-wrap gap-2.5">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2.5 rounded-xl text-xs transition-all duration-200 font-medium border flex items-center gap-2 cursor-pointer ${
                selectedCategory === "all" 
                  ? "bg-amber-500 text-slate-950 border-amber-400 font-bold shadow-md shadow-amber-500/15"
                  : "bg-gray-900/30 text-gray-400 border-slate-800/60 hover:text-white hover:bg-slate-900 hover:-translate-y-0.5"
              }`}
            >
              <span>All Instruments</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${selectedCategory === "all" ? "bg-slate-950/20 text-slate-950 font-bold" : "bg-slate-950/40 text-slate-400"}`}>
                {allTools.length}
              </span>
            </button>
            {categories.map((cat) => {
              const count = categoryCounts[cat.id] || 0;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2.5 rounded-xl text-xs transition-all duration-200 font-medium border flex items-center gap-2 cursor-pointer ${
                    selectedCategory === cat.id
                      ? "bg-amber-500 text-slate-950 border-amber-400 font-bold shadow-md shadow-amber-500/15"
                      : "bg-gray-900/30 text-gray-400 border-slate-800/60 hover:text-slate-100 hover:bg-slate-900 hover:-translate-y-0.5"
                  }`}
                >
                  <CategoryIcon iconName={cat.icon} className="w-3.5 h-3.5" />
                  <span>{cat.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${selectedCategory === cat.id ? "bg-slate-950/20 text-slate-950 font-bold" : "bg-slate-950/40 text-slate-500"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

      </div>

      {/* Default/Spotlight section (if no searching active) */}
      {searchQuery === "" && selectedCategory === "all" && (
        <div className="space-y-8 pt-4 text-left">
          <header className="flex items-center gap-2.5 border-b border-slate-800 pb-4">
            <Award className="w-6 h-6 text-amber-500" />
            <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight font-sans">
              Featured Financial Calculators
            </h3>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in items-stretch">
            {spotlightTools.map((tool) => {
              const slug = getSlug(tool.name);
              const badge = getToolBadge(tool.id);
              return (
                <Link 
                  key={tool.id}
                  to={`/${slug}`}
                  className="group relative cursor-pointer border border-[#1E293B] bg-[#0c1322] hover:bg-[#121c31] rounded-[16px] p-8 transition-all duration-300 hover:border-slate-700 hover:-translate-y-1 shadow-md hover:shadow-xl hover:shadow-black/30 flex flex-col justify-between h-full min-h-[17.5rem]"
                >
                  <div className="flex flex-col h-full justify-between text-left space-y-4">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div className="w-12 h-12 rounded-xl bg-slate-800/85 flex items-center justify-center border border-slate-700/60 shadow-sm text-amber-500">
                        <CategoryIcon 
                          iconName={categories.find(c => c.id === tool.category)?.icon || "Calculator"} 
                          className="w-6 h-6 text-amber-500" 
                          categoryId={tool.category} 
                          toolName={tool.name}
                        />
                      </div>
                      {badge && (
                        <span className={`text-xs font-semibold tracking-wide px-3 py-1 rounded-full border shadow-sm select-none ${badge.className}`}>
                          {badge.text}
                        </span>
                      )}
                    </div>
                    <h4 className="font-sans font-bold text-white group-hover:text-amber-400 transition-colors text-[24px] leading-snug tracking-tight">
                      {tool.name}
                    </h4>
                    <p className="text-slate-200 text-[17.5px] leading-[1.9] font-normal min-h-[100px] line-clamp-3">
                      {tool.description}
                    </p>
                    <div className="pt-5 mt-auto border-t border-slate-800/60 flex items-center justify-between text-base font-semibold text-amber-400 group-hover:text-amber-300 transition-colors">
                      <span className="font-sans font-semibold tracking-wide text-base">Calculate Now</span>
                      <ChevronRight className="w-5 h-5 text-amber-500 group-hover:translate-x-1.5 transition-transform duration-300" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Primary Grid Listings Grouped by Section Headers */}
      <div className="space-y-12 text-left pt-6">
        <header className="border-b border-slate-800 pb-4">
          <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight font-sans">
            Explore Our Financial Calculators
          </h3>
        </header>

        {groupedTools.length > 0 ? (
          <div className="space-y-14">
            {groupedTools.map(({ category, tools }) => {
              const details = categoryHeaderDetails[category.id] || {
                title: `${category.name} Calculators`,
                desc: category.description
              };
              return (
                <div key={category.id} className="space-y-6 pt-2">
                  <header className="border-l-4 border-amber-500 pl-4 space-y-1">
                    <h4 className="text-xl sm:text-2xl font-bold text-white tracking-tight font-sans">
                      {details.title}
                    </h4>
                    <p className="text-sm sm:text-base text-slate-400 leading-relaxed font-light">
                      {details.desc}
                    </p>
                  </header>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                    {tools.map((tool) => {
                      const slug = getSlug(tool.name);
                      const badge = getToolBadge(tool.id);
                      return (
                        <Link
                          key={tool.id}
                          to={`/${slug}`}
                          className="group relative cursor-pointer border border-[#1E293B] bg-[#0c1322] hover:bg-[#121c31] rounded-[16px] p-8 transition-all duration-300 hover:border-slate-700 hover:-translate-y-1 shadow-md hover:shadow-xl hover:shadow-black/30 flex flex-col justify-between h-full min-h-[17.5rem]"
                        >
                          <div className="flex flex-col h-full justify-between text-left space-y-4">
                            <div className="flex items-center justify-between gap-4 mb-2">
                              <div className="w-12 h-12 rounded-xl bg-slate-800/85 flex items-center justify-center border border-slate-700/60 shadow-sm text-amber-500">
                                <CategoryIcon 
                                  iconName={categories.find(c => c.id === tool.category)?.icon || "Calculator"} 
                                  className="w-6 h-6 text-amber-500" 
                                  categoryId={tool.category} 
                                  toolName={tool.name}
                                />
                              </div>
                              {badge && (
                                <span className={`text-xs font-semibold tracking-wide px-3 py-1 rounded-full border shadow-sm select-none ${badge.className}`}>
                                  {badge.text}
                                  </span>
                              )}
                            </div>
                            <h4 className="font-sans font-bold text-white group-hover:text-amber-400 transition-colors text-[24px] leading-snug tracking-tight">
                              {tool.name}
                            </h4>
                            <p className="text-slate-200 text-[17.5px] leading-[1.9] font-normal min-h-[100px] line-clamp-3">
                              {tool.description}
                            </p>
                            <div className="pt-5 mt-auto border-t border-slate-800/60 flex items-center justify-between text-base font-semibold text-amber-400 group-hover:text-amber-300 transition-colors">
                              <span className="font-sans font-semibold tracking-wide text-base">Calculate Now</span>
                              <ChevronRight className="w-5 h-5 text-amber-500 group-hover:translate-x-1.5 transition-transform duration-300" />
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 border border-dashed border-slate-800 rounded-xl bg-slate-950/20 select-none">
            <p className="text-sm text-slate-400 font-sans tracking-wide">
              NO CALCULATORS MATCHED YOUR ENTIRE SEARCH CRITERIA
            </p>
            <button 
              onClick={() => { setSearchQuery(""); setSelectedCategory("all"); }}
              className="mt-4 text-sm font-semibold text-amber-400 hover:text-amber-300 hover:underline cursor-pointer bg-transparent border-none"
            >
              Reset filters &amp; directory search
            </button>
          </div>
        )}
      </div>

      {/* ================= GENERAL SOLUTIONS FAQ & CONTACT ================= */}
      <div id="faq-section" className="pt-16 border-t border-slate-900 mt-20 space-y-12 text-left">
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-amber-500/20 bg-amber-950/20 text-xs font-mono text-amber-400">
              <HelpCircle className="w-3.5 h-3.5 text-amber-500" />
              Frequently Answered Inquiries
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-display font-medium text-white tracking-tight">
            General Solutions FAQ
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm max-w-xl mx-auto font-light leading-relaxed">
            Understand how our dynamic client-side calculators process variables and ensure bank-grade compliance.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {[
            {
              q: "Is USMoneyAI free for everyone?",
              a: "Yes, USMoneyAI provides completely free access to all 77+ financial estimators and calculators. We do not require any credit card registration or subscription fees to access our tools."
            },
            {
              q: "How accurate are the estimations on this platform?",
              a: "All calculations are based on standard financial mathematics, amortizations, and compliant tax models. While formulas are updated regularly to ensure accuracy, please consult a certified financial planner for personal advice."
            },
            {
              q: "Is my personal data secure in the app?",
              a: "Absolutely. USMoneyAI operates entirely client-side for calculations; we do not store, upload, or sell your private financial inputs. Your data remains secure in your personal browser session."
            },
            {
              q: "Can I use USMoneyAI on my mobile tablet or smartphone?",
              a: "Yes! The entire platform is built with responsive grid layouts and mobile-first touch interfaces, delivering a smooth, fast experience on any iOS, Android, or tablet browser."
            }
          ].map((faq, idx) => {
            const isOpen = openFaqIndex === idx;
            return (
              <div 
                key={idx}
                className="bg-[#040914] border border-slate-900 rounded-2xl overflow-hidden transition-all duration-300"
                id={`faq-item-${idx}`}
              >
                <button
                  onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between p-5 text-left font-medium text-slate-100 hover:text-white hover:bg-slate-900/40 transition-colors focus:outline-none"
                  id={`faq-btn-${idx}`}
                >
                  <span className="text-sm font-medium tracking-tight pr-4">
                    {faq.q}
                  </span>
                  <div className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 border border-slate-800 transition-transform duration-300 ${isOpen ? "rotate-180 border-cyan-500/30 text-cyan-400" : "text-slate-400"}`}>
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </button>

                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    isOpen 
                      ? "max-h-[800px] opacity-100 border-t border-slate-900/40" 
                      : "max-h-0 opacity-0 pointer-events-none"
                  }`}
                >
                  <div className="p-5 text-xs sm:text-sm text-slate-400 leading-relaxed font-light bg-slate-950/20">
                    {faq.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Help / Contact Card */}
        <div className="max-w-3xl mx-auto rounded-2xl border border-dashed border-cyan-900/40 bg-[#020712]/50 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-sm font-semibold text-white">Have custom corporate or regulatory formula inquiries?</h4>
            <p className="text-xs text-slate-400 font-light">Get custom mathematical implementations integrated with our live API dashboards.</p>
          </div>
          <Link
            to="/contact"
            className="px-5 py-2.5 bg-cyan-950/60 border border-cyan-800/80 hover:bg-cyan-950/90 text-cyan-400 font-semibold rounded-xl text-xs transition-colors shrink-0 flex items-center gap-2"
          >
            <span>Contact Support</span>
            <span className="text-emerald-500">● Live</span>
          </Link>
        </div>

      </div>

    </div>
  );
}
